import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fillTemplate } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function OrderTemplatesPanel({ order }) {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/templates").then(({ data }) => setTemplates(data)).catch(() => {});
  }, []);

  if (templates.length === 0) return null;

  const selected = templates.find((t) => t.id === selectedId);
  const filled = selected ? fillTemplate(selected.body, order) : "";

  const copy = async () => {
    if (!selected) return;
    try {
      await navigator.clipboard.writeText(filled);
      setCopied(true);
      toast.success(`Copied “${selected.name}” with property info`);
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Couldn't copy — select the text manually"); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="order-templates-panel">
      <span className="overline text-muted-foreground flex items-center gap-1.5">
        <FileText className="h-3.5 w-3.5 text-primary" /> Templates
      </span>
      <p className="mt-1 text-xs text-muted-foreground">Pick a template — it fills in this property's address & client automatically.</p>

      <div className="mt-3">
        <Select value={selectedId} onValueChange={(v) => { setSelectedId(v); setCopied(false); }}>
          <SelectTrigger data-testid="order-template-select" className="h-9">
            <SelectValue placeholder="Choose a template…" />
          </SelectTrigger>
          <SelectContent>
            {templates.map((t) => (
              <SelectItem key={t.id} value={t.id} data-testid={`order-template-option-${t.id}`}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selected && (
        <div className="mt-3" data-testid="order-template-preview">
          <div className="mb-2 flex justify-end">
            <button onClick={copy} data-testid="order-template-copy-btn"
              className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-xs transition-colors hover:border-primary/50">
              {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto whitespace-pre-wrap rounded-lg border border-border bg-secondary/20 p-3 text-sm text-foreground/90"
            data-testid="order-template-body">
            {filled || <span className="text-muted-foreground">This template is empty.</span>}
          </div>
        </div>
      )}
    </div>
  );
}
